import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { AdminSidebar } from '@/components/layout/AdminSidebar'

interface RecentEpisode {
  id: number
  slug: string
  title: string
  status: string
  duration: string
}

interface Props {
  stats: { mrr: number; pro_users: number; episodes_count: number; published_count: number; total_views: number }
  recent_episodes: RecentEpisode[]
  pipeline: { draft: number; scheduled: number; published: number }
}

export default function Overview({ stats, recent_episodes, pipeline }: Props) {
  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar active="/admin" />

      <main className="flex-1 p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Overview</h1>
          <Link href="/admin/episodes/new">
            <Button>New episode</Button>
          </Link>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <StatCard label="MRR" value={`$${stats.mrr}`} />
          <StatCard label="PRO USERS" value={String(stats.pro_users)} />
          <StatCard label="EPISODES" value={String(stats.episodes_count)} />
          <StatCard label="TOTAL VIEWS" value={stats.total_views.toLocaleString()} highlight />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Content Pipeline */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="font-bold text-gray-900 text-lg mb-4">Content Pipeline</h3>
            <div className="space-y-1.5 text-sm text-gray-600 font-mono">
              <p>Draft: {pipeline.draft}</p>
              <p>Scheduled: {pipeline.scheduled}</p>
              <p>Published: {pipeline.published}</p>
            </div>
          </div>

          {/* User Actions */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="font-bold text-gray-900 text-lg mb-4">User Actions</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              {['Manual Pro grant', 'Failed payment review', 'GitHub OAuth audit', 'Export subscribers', 'Moderate comments'].map(a => (
                <li key={a}><Link href="/admin/users" className="hover:text-brand">{a}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        {/* Recent episodes */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Recent episodes</h3>
            <Link href="/admin/episodes" className="text-sm text-brand hover:underline">View all →</Link>
          </div>
          <table className="w-full text-sm font-mono">
            <tbody className="divide-y divide-gray-50">
              {recent_episodes.map(ep => (
                <tr key={ep.id}>
                  <td className="py-2 text-gray-700">{ep.title}</td>
                  <td className="py-2 text-gray-400 capitalize w-28">{ep.status}</td>
                  <td className="py-2 text-gray-400 w-20">{ep.duration}</td>
                  <td className="py-2 w-20 text-right">
                    <Link href={`/admin/episodes/${ep.slug}/edit`} className="text-brand hover:underline text-xs">Edit</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}

function StatCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-5 ${highlight ? 'bg-brand border-brand text-white' : 'bg-white border-gray-100'}`}>
      <p className={`text-xs font-semibold tracking-widest mb-2 ${highlight ? 'text-white/80' : 'text-gray-400'}`}>{label}</p>
      <p className={`text-3xl font-bold font-mono ${highlight ? 'text-white' : 'text-gray-900'}`}>{value}</p>
    </div>
  )
}
